import { APP_BASE_HREF } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Component, Inject, OnInit, ViewContainerRef, DOCUMENT } from '@angular/core';
import { DomHandler } from 'primeng/dom';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

// DO NOT REMOVE, we need it for pendo service
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { SystemNotificationService } from '../../../Admin/SystemNotifications/services/system-notification.service';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { GlobalMessageService } from '../../services/global-message.service';

import { UserStateModel } from '../../../Core/Users/models/user-state.model';
import { ConfigModel } from '../../../Core/Config/models/config.model';
import { UserModel } from '../../../Core/Users/models/user.model';
import { AccountModel } from '../../../Core/Users/models/account.model';
import { LayoutTypesEnum } from '../../../Common/Layout/enums/layout-types.enum';
import { SystemNotificationModel } from '../../../Admin/SystemNotifications/models/system-notification.model';
import { SystemNotificationsTypeEnum } from '../../../Admin/SystemNotifications/enums/system-notifications-type.enum';
import { SystemNotificationsStatusEnum } from '../../../Admin/SystemNotifications/enums/system-notifications-status.enum';

import { NavItem } from '@Common/Layout/components/Navbar/navbar.component';

import { primaryNav } from '../../config/primaryNavigation/primary-nav.config';
import { resellerLeftNav } from '../../config/primaryNavigation/resellers/reseller-left-nav.config';
import { newsDirectSupportNav } from '../../config/supportNavigation/resellers/news-direct-support-nav.config';
import { burrellesSupportNav } from '../../config/supportNavigation/resellers/burrelles-support-nav.config';
import { kantarSupportNav } from '../../config/supportNavigation/resellers/kantar-support-nav.config';
import { uniceptaSupportNav } from '../../config/supportNavigation/resellers/unicepta-support-nav.config';
import { nexisSupportNav } from '../../config/supportNavigation/resellers/nexis-support-nav.config';
import { paMediaSupportNav } from '../../config/supportNavigation/resellers/pa-support-nav.config';
import { refmediaSupportNav } from '../../config/supportNavigation/resellers/refmedia-support-nav.config';
import { ActionKeys } from '../../../Common/Forms/enums/keys.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { PartnerResellers } from '../../../Core/Config/enums/partner-resellers.enum';
import { partnerResellerConfigs, ResellerConfig } from 'app/Bootstrap/config/resellers/reseller-config-model';

@Component({
    selector: 'agpr-bootstrap',
    templateUrl: 'bootstrap.component.html',
    styleUrls: ['bootstrap.component.scss'],
    standalone: false
})
export class BootstrapComponent implements OnInit {

    layout: LayoutTypesEnum = LayoutTypesEnum.vertical;

    readonly logoUrl: string;
    logoRouterLink: string[] = ['/Dashboard'];

    primaryNav: {left: NavItem[]; right: NavItem[]; supportLinks: NavItem[]} = primaryNav;

    userState: UserStateModel;
    user: UserModel;
    account: AccountModel;
    isImpersonating: boolean = false;

    intercomUnreadCount: number = 1;

    private subscriptions: Subscription[];

    notifications: SystemNotificationModel[] = [];
    maintenanceWarnings: SystemNotificationModel[] = [];
    nextFetch;

    showMasterLayout: boolean = true;

    readonly oneDayInMiliSec = 1000 * 60 * 60 * 24;

    showIntercom: boolean = true;
    showWelcomeMessage: boolean = false;
    dashboardWelcomeMessage: string;
    zIndex: number;

    suppportLinkMap: Map<string, NavItem[]> = new Map([
        [ConfigService.burrellesLuceGUID, burrellesSupportNav],
        [ConfigService.kantar, kantarSupportNav],
        [ConfigService.lexisNexisGUID, nexisSupportNav],
        [ConfigService.newsDirect, newsDirectSupportNav],
        [ConfigService.unicepta, uniceptaSupportNav],
        [ConfigService.paMedia, paMediaSupportNav],
        [ConfigService.refmedia, refmediaSupportNav]
    ]);

    currentUrl: string = '';
    skipLinkPath: string;

    resellerConfig: ResellerConfig;
    partnerReseller: PartnerResellers;


    constructor(
        public viewContainer: ViewContainerRef,
        private configService: ConfigService,
        private currentUserStateService: CurrentUserStateService,
        private systemNotificationService: SystemNotificationService,
        private globalMessageService: GlobalMessageService, // add it to be initialized
        private localizationService: LocalizationService,
        private router: Router,
        private coreDialogService: CoreDialogService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        @Inject(DOCUMENT) public document: Document,
        // DO NOT REMOVE, we need it for pendo service
        private monitorConfigService: MonitorConfigService
    ) {
        const isReseller: boolean = this.configService.hasBrandGUID();
        const isNewsDirect: boolean = this.configService.brandGUIDIsNewsDirect();
        const isLexisNexis: boolean = this.configService.brandGUIDIsLexisNexis();
        const isBurrelles: boolean = this.configService.brandGUIDIsBurrelles();
        const isPAMedia: boolean = this.configService.brandGUIDIsPaMedia();
        const isRefMedia: boolean = this.configService.brandGUIDIsRefMedia();

        if (isRefMedia) {
            const faviconElement: HTMLLinkElement = document.querySelector('#favicon');
            faviconElement.href = 'assets/images/RefMedia-FavIcon.jpg';
        }

        this.checkTermsAccepted();

        this.showIntercom = !isReseller || isNewsDirect || isLexisNexis || isBurrelles || isPAMedia;
        this.logoUrl = appBaseHref + 'assets/images/agility-logo-horizontal.png';
        this.partnerReseller = this.configService.getPartnerReseller();
        this.resellerConfig = partnerResellerConfigs.get(this.partnerReseller);

        if (this.configService.brandGUIDIsRefMedia()) {
            this.logoUrl = appBaseHref + this.resellerConfig.logoUrl;
        }

        this.zIndex = ++DomHandler.zindex;

        this.coreDialogService.viewContainer = this.viewContainer;
    }

    ngOnInit() {
        this.installIntercom();
        this.createSubscriptions();
        this.setNavigation();

        this.router.events
            .pipe(
                filter(
                    event => event instanceof NavigationEnd
                )
            )
            .subscribe(() => {
                if (!this.router.url.endsWith('#main-content')) {
                    if (this.appBaseHref !== '/') {
                        let routerUrl = this.router.url;
                        if (this.router.url.charAt(0) === '/') {
                            routerUrl = routerUrl.substring(1);
                        }
                        this.skipLinkPath = `${this.appBaseHref + routerUrl}#main-content`;
                    } else {
                        this.skipLinkPath = `${this.router.url}#main-content`;
                    }
                }
            });
    }

    checkTermsAccepted() {
        // we have to use getState here instead of combineLatest[getAccount(),getPreferences()]..
        // because when we would change the state, the combine latest would "trigger" for every change, and the
        // getAccount() would have the updated data, but getPreferences would not.. so it was causing some issues
        // (would moslty happen on ghost in / ghost out)
        this.currentUserStateService.getState().pipe(filter(x => !!x)).subscribe(
            userState => {
                // we do not want to show the terms page if the user is ghosted in (hence the !impersonatorUser)
                if (userState?.account?.neptuneOnly && !userState?.preferences?.tosAccepted && !userState?.impersonator) {
                    this.router.navigate(['Terms']);
                    return;
                }
                // we want to redirect the user to change their password on a login
                if (userState?.account?.neptuneOnly && userState?.preferences?.hasTempPassword && !userState?.impersonator) {
                    this.router.navigate(['SetPassword']);
                    return;
                }
            }
        );
    }

    setNavigation() {
        if (this.configService.hasBrandGUID()) {
            this.primaryNav.left = resellerLeftNav;
            this.primaryNav.supportLinks = this.suppportLinkMap.get(this.configService.getBrandGUID());
            // Hide change password for PA white label
            if (this.configService.brandGUIDIsPaMedia()) {
                const accountIndex = this.primaryNav.right.findIndex(item => item.id === 'primaryNavAccounts');
                this.primaryNav.right[accountIndex].items
                    = this.primaryNav.right[accountIndex].items.filter(item => item.id !== 'primaryNavChangePassword');
            }
        }
    }

    /**
     * Creates the subscriptions this component relies on
     */
    private createSubscriptions(): void {
        this.subscriptions = [];

        this.subscriptions.push(
            this.currentUserStateService.getState().subscribe(
                userState => this.onUserStateChange(userState)
            ),
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationEnd) {
                    const urlParseMasterLayout: RegExp = /PasswordReset|Login|login|ForgotPassword|Terms|MultiFactorAuthentication/;
                    const matchesMasterLayout = e.urlAfterRedirects.match(urlParseMasterLayout);
                    if (matchesMasterLayout) {
                        this.showMasterLayout = false;
                    } else {
                        this.showMasterLayout = true;
                    }
                    const urlWelcomeMessage: RegExp = /Dashboard|dashboard/;
                    const otherDashboards: RegExp = /distributions|subscriptions|topics|mentions|search/;
                    this.showWelcomeMessage = !!e.urlAfterRedirects.match(urlWelcomeMessage) && !e.urlAfterRedirects.match(otherDashboards);
                }
            }),
            this.localizationService.getLocalizationMapChange().subscribe(() => {
                this.document.documentElement.lang = this.localizationService.uiLanguageTag;
            })
        );
    }


    //#region Intercom
    /** Embed intercom js script to the head element */
    installIntercom() {
        const head = document.getElementsByTagName('head')[0];
        const script = document.createElement('script');
        // eslint-disable-next-line max-len
        script.innerHTML = `var APP_ID = "${this.getIntercomID()}"; (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic(\'reattach_activator\');ic(\'update\',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement(\'script\');s.type=\'text/javascript\';s.async=true;s.src=\'https://widget.intercom.io/widget/\' + APP_ID;var x=d.getElementsByTagName(\'script\')[0];x.parentNode.insertBefore(s, x);};if(document.readyState===\'complete\'){l();}else if(w.attachEvent){w.attachEvent(\'onload\',l);}else{w.addEventListener(\'load\',l,false);}}})();`;

        head.insertBefore(script, head.firstChild);

        if (!this.showIntercom) {
            window.Intercom('shutdown');
        }

        this.startIntercom();
    }

    /**
     * Starts Intercom
     */
    private startIntercom(): void {
        const intercomID: string = this.getIntercomID();

        /* eslint-disable @typescript-eslint/naming-convention */
        // Rule doesn't apply here because external resource used
        window.Intercom('boot', {
            app_id: intercomID,
            custom_launcher_selector: '#agpr-intercom',
            hide_default_launcher: true
        });

        /* eslint-enable @typescript-eslint/naming-convention */
        window.Intercom('onUnreadCountChange', (count => {
            this.intercomUnreadCount = count;
        }));
        window.Intercom('onHide', (() => document.getElementById('agpr-intercom').focus()));
    }

    /**
     * Updates the intercom instance with the user info
     */
    private sendIntercomUserInfo(user: UserModel, account: AccountModel): void {
        const name: string = user.getDisplayName();
        const email: string = user.username;

        window.Intercom('update',{
            userID: user.id,
            customerID: account.id,
            name: name,
            email: email,
            customerName: account.name
        });
    }

    private getIntercomID() {
        const config: any = this.configService.getConfig();
        const hasConfig: boolean = config instanceof ConfigModel;
        let intercomID: string = 'a4ns5805';

        if (hasConfig && config.intercomID) {
            intercomID = config.intercomID;
        }

        return intercomID;
    }

    intercomKeyup(event: KeyboardEvent) {
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE) {
            window.Intercom('show');
        } else if (event.key === ActionKeys.ESCAPE) {
            window.Intercom('hide');
        }
    }
    //#endregion

    /**
     * Responds to changes in the user state
     */
    private onUserStateChange(userState: UserStateModel): void {
        if (!userState) {
            return;
        }

        this.userState = userState;
        this.user = userState.user;
        this.account = userState.account;
        this.isImpersonating = !!userState.impersonator;

        this.sendIntercomUserInfo(userState.user, userState.account);

        // handle system notifications
        const notifications = userState.notifications?.filter(notification => !sessionStorage.getItem(notification.getSessionStorageKey()));
        this.notifications = [];
        if (notifications?.length) {
            notifications
                .forEach(notification => {
                    if (notification.statusBadge.status === SystemNotificationsStatusEnum.pending) {
                        const interval = notification.notificationTimeRange.startConvertedwithTz.getTime() - new Date().getTime();
                        // If the timeout is longer than a day, don't bother setting it
                        // setting a timeout of greater than 2147483647 results in an overflow and an instant timeout
                        if ((interval > 0 ) && (interval <= this.oneDayInMiliSec)) {
                            setTimeout(() => this.currentUserStateService.loadUserState(), interval);
                        }
                    }
                    if (notification.statusBadge.status === SystemNotificationsStatusEnum.active) {
                        const interval = notification.notificationTimeRange.endConvertedwithTz.getTime() - new Date().getTime();
                        if ((interval > 0 ) && (interval <= this.oneDayInMiliSec)) {
                            setTimeout(() => this.currentUserStateService.loadUserState(), interval);
                        }
                    }
                });
            this.sortNotifications(notifications.filter(x => x.statusBadge.status === SystemNotificationsStatusEnum.active));

            const maintenanceNotifications = notifications.filter(item => item.type === SystemNotificationsTypeEnum.maintenance);
            if (maintenanceNotifications) {
                maintenanceNotifications.forEach(item => {
                    this.createMaintenanceWarnings(
                        item.maintenanceTimeRange.startConvertedwithTz.getTime(),
                        item.maintenanceTimeRange.endConvertedwithTz.getTime()
                    );
                });
            }
        }
        this.setDashboardWelcomeMessage();
    }

    // NOTIFICATION REGION

    /**
     * Sort notifications based on requirements
     */
    sortNotifications(array: SystemNotificationModel[]) {
        const groupedArray = [];
        let arrayToSort = [];

        // group array based on notification type
        for (let i = 0; i < array.length; i++) {
            if (!groupedArray[array[i].type]) {
                groupedArray[array[i].type] = [];
            }
            groupedArray[array[i].type].push(array[i]);
        }

        // 'Important' group is always on top of the array -> then Critical
        arrayToSort = [
            groupedArray[SystemNotificationsTypeEnum.important],
            groupedArray[SystemNotificationsTypeEnum.critical],
            groupedArray[SystemNotificationsTypeEnum.info],
            groupedArray[SystemNotificationsTypeEnum.maintenance]
        ];

        // within each group, sort item by its created/modified date
        for (const type in arrayToSort) {
            if (arrayToSort.hasOwnProperty(type) && arrayToSort[type]) {
                arrayToSort[type] = arrayToSort[type].sort(
                    (a, b) => {
                        const bSortDate = b.modifiedDate ? +b.modifiedDate : +b.createdDate;
                        const aSortDate = a.modifiedDate ? +a.modifiedDate : +a.createdDate;
                        return bSortDate - aSortDate;
                    }
                );
                arrayToSort[type].forEach(item => this.notifications.push(item));
            }
        }
    }

    createMaintenanceWarnings(startTime: number, endTime: number) {
        if (endTime - new Date().getTime() <= 0) {
            return;
        };
        const warning: SystemNotificationModel = new SystemNotificationModel();
        const fifteenMinPrior = (startTime - 900000) - new Date().getTime();
        const displayStartTime = new Date(startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short'});

        // display start warning immediately
        if (startTime - new Date().getTime() <= 0) {
            this.maintenanceWarnings = [];
            warning.message = this.localizationService.transform('SystemNotification.maintenanceWarnings.start', {
                startTime: displayStartTime
            });
            warning.type = SystemNotificationsTypeEnum.maintenance;
            this.maintenanceWarnings.push(warning);
            // Set timeout to delete warning when maintenance period is over
            // If the timeout is longer than a day, don't bother setting it
            // setting a timeout of greater than 2147483647 results in an overflow and an instant timeout
            const interval = endTime - new Date().getTime();
            if (interval <= this.oneDayInMiliSec) {
                setTimeout(() => {
                    const index = this.maintenanceWarnings.indexOf(warning);
                    this.maintenanceWarnings.splice(index, 1);
                }, interval);
            }
            return;
        }

        setTimeout(() => {
            this.maintenanceWarnings = [];
            // 5 minute warning
            warning.message = this.localizationService.transform('SystemNotification.maintenanceWarnings.fifteenMin', {
                startTime: displayStartTime
            });
            warning.type = SystemNotificationsTypeEnum.maintenance;
            this.maintenanceWarnings.push(warning);
            // start warning
            setTimeout(() => {
                warning.message = this.localizationService.transform('SystemNotification.maintenanceWarnings.start', {
                    startTime: displayStartTime
                });
                warning.type = SystemNotificationsTypeEnum.maintenance;

                // Set timeout to delete warning when maintenance period is over
                const interval = endTime - new Date().getTime();
                if (interval <= this.oneDayInMiliSec) {
                    setTimeout(() => {
                        const index = this.maintenanceWarnings.indexOf(warning);
                        this.maintenanceWarnings.splice(index, 1);
                    }, interval);
                }
            }, startTime - new Date().getTime());
        }, fifteenMinPrior);
    }
    // #endregion

    /**
     * This function binds every click (or mousedown) event within the <agpr-master-layout> component
     * to the angular click event handler.  Its purpose in this case is to register
     * all click (and mousedown) events to the custom event handler plugin defined in ClickEventHandlerService
     */
    emptyHandler() {}


    handleNotificationUpdate(notification: SystemNotificationModel){
        this.systemNotificationService.updateNotification(notification).subscribe();
    }

    handleDismissal(notification: SystemNotificationModel) {
        sessionStorage.setItem(notification.getSessionStorageKey(), 'true');
    }

    setDashboardWelcomeMessage() {
        this.dashboardWelcomeMessage = this.localizationService.transform('PrimaryNav.dashboardWelcomeMessage',
            {
                userName: this.user.getDisplayName(),
                accountName: this.account.name
            });
    }
}
